/**
 * Shared Event Card Component
 * Used by both EventLogPage and LiveEventMonitor
 */

import React from 'react';
import { Event } from '../lib/types';
import { getEventIcon, getTimeAgo, resolveNotificationImageUrl } from '../lib/utils';

interface EventCardProps {
  event: Event & {
    _providers?: string[];
    _deliveryCount?: number;
    _anyForwarded?: boolean;
    _providerId?: string | null;
  };
  isExpanded?: boolean;
  onToggleDetails?: (eventId: string) => void;
  showNewBadge?: boolean;
}

const severityColors: Record<string, string> = {
  critical: 'border-l-red-500 bg-red-50',
  high: 'border-l-orange-500 bg-orange-50',
  medium: 'border-l-yellow-500 bg-yellow-50',
  low: 'border-l-blue-500 bg-blue-50',
  info: 'border-l-gray-500 bg-gray-50',
};

const severityBadgeColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
  info: 'bg-gray-100 text-gray-800',
};

export const EventCard: React.FC<EventCardProps> = ({
  event,
  isExpanded = false,
  onToggleDetails,
  showNewBadge = true,
}) => {
  const payload = event.event_payload;
  const severity = payload.notification?.severity || 'info';
  const eventType = payload.notification?.type || payload.eventType || 'UNKNOWN';
  const timestamp = payload.timestamp || event.created_at;
  const imageUrl = resolveNotificationImageUrl(payload.notification?.imageUrl);
  const providers = event._providers && event._providers.length > 0
    ? event._providers
    : event._providerId
      ? [event._providerId]
      : [];
  const deliveryCount = event._deliveryCount ?? providers.length;

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${
        severityColors[severity] || severityColors.info
      }`}
    >
      <div className="flex flex-col md:flex-row gap-4">
        {imageUrl && (
          <div className="flex-shrink-0 w-full md:w-56 lg:w-64">
            <img
              src={imageUrl}
              alt="Event Status"
              className="w-full h-auto rounded-lg border border-gray-300 shadow-sm"
            />
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <img src={getEventIcon(eventType)} alt={eventType} className="w-6 h-6" />
              <strong className="text-lg">{eventType.replace(/_/g, ' ')}</strong>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded ${
                  severityBadgeColors[severity] || severityBadgeColors.info
                }`}
              >
                {severity.toUpperCase()}
              </span>
              {showNewBadge && !event.seen && (
                <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-500 text-white">
                  NEW
                </span>
              )}
              {providers.length > 0 && (
                <span
                  className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800"
                  title={`Delivered to ${providers.length} partner(s): ${providers.join(', ')}${
                    deliveryCount > providers.length ? ` (${deliveryCount} total deliveries)` : ''
                  }`}
                >
                  {providers.length === 1
                    ? `→ ${providers[0]}`
                    : `→ ${providers.length} partners`}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-600 text-right">
              {new Date(timestamp).toLocaleString()}
              <span className="block text-xs">({getTimeAgo(timestamp)})</span>
            </div>
          </div>

          {payload.notification && (
            <div className="text-sm space-y-1 mb-3">
              {payload.notification.client && (
                <div>
                  <strong>Client:</strong> {payload.notification.client}
                </div>
              )}
              {payload.notification.wetness !== undefined && (
                <div>💧 Wetness: {payload.notification.wetness}%</div>
              )}
              {payload.notification.battery !== undefined && (
                <div>🔋 Battery: {payload.notification.battery}%</div>
              )}
              {payload.notification.changeReason && (
                <div>
                  <strong>Reason:</strong> {payload.notification.changeReason}
                </div>
              )}
              {payload.notification.message && (
                <div className="bg-white p-2 rounded italic text-gray-700 mt-2">
                  {payload.notification.message}
                </div>
              )}
            </div>
          )}

          {onToggleDetails && (
            <button
              onClick={() => onToggleDetails(event.timestamp_eventid)}
              className="text-sm text-primary hover:underline"
            >
              {isExpanded ? '▼ Hide Details' : '▶ Show Full Event Payload'}
            </button>
          )}

          {isExpanded && onToggleDetails && (
            <div className="mt-3 bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto">
              <pre className="text-xs">{JSON.stringify(payload, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

